// src/services/OfflineService.ts
import { supabase } from '../lib/supabaseClient';

// Use a simple type definition
interface Trade {
  id?: string;
  [key: string]: any; // Allow all properties
}

export class OfflineService {
  // Store trades locally when offline
  async saveTrade(trade: Trade): Promise<Trade> {
    try {
      // Use any to bypass TypeScript's static analysis for Supabase return
      const { error } = await supabase.from('trades').insert(trade) as any;
      
      if (error) {
        throw new Error(error.message);
      }
      
      return trade; // Just return the original trade on success
    } catch (e) {
      // Save to localStorage on error
      const offlineTradesStr = localStorage.getItem('offlineTrades');
      let offlineTrades: Trade[] = [];
      
      if (offlineTradesStr) {
        try {
          offlineTrades = JSON.parse(offlineTradesStr);
        } catch {
          offlineTrades = [];
        }
      }
      
      // Add offline ID
      const offlineTrade = { ...trade, offlineId: Date.now() };
      offlineTrades.push(offlineTrade);
      
      localStorage.setItem('offlineTrades', JSON.stringify(offlineTrades));
      return offlineTrade;
    }
  }

  // Sync offline trades
  async syncOfflineTrades(): Promise<void> {
    const offlineTradesStr = localStorage.getItem('offlineTrades');
    if (!offlineTradesStr) return;
    
    let offlineTrades: Trade[] = [];
    try {
      offlineTrades = JSON.parse(offlineTradesStr);
    } catch {
      return; // Invalid JSON, can't sync
    }
    
    if (!offlineTrades || offlineTrades.length === 0) return;
    
    // Track which trades to keep (failed syncs)
    const keepTrades: Trade[] = [];
    
    // Process each trade
    for (let i = 0; i < offlineTrades.length; i++) {
      const trade = offlineTrades[i];
      if (!trade) continue;
      
      const { offlineId, ...syncTrade } = trade;
      
      try {
        const { error } = await supabase.from('trades').insert(syncTrade) as any;
        if (error) {
          keepTrades.push(trade);
        }
      } catch (e) {
        keepTrades.push(trade);
      }
    }
    
    // Update local storage with remaining trades
    localStorage.setItem('offlineTrades', JSON.stringify(keepTrades));
  }

  // Helper methods
  hasOfflineTrades(): boolean {
    const trades = this.getOfflineTrades();
    return trades.length > 0;
  }
  
  getOfflineTradesCount(): number {
    return this.getOfflineTrades().length;
  }
  
  private getOfflineTrades(): Trade[] {
    try {
      const data = localStorage.getItem('offlineTrades');
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }
}